<script setup lang="ts">
import type { FlowLayoutSize } from "@/components/controls/BmFormFlowLayout.vue";
import BmFormFlowLayout, {
  flowLayoutSizes,
} from "@/components/controls/BmFormFlowLayout.vue";
import BmEnumDdl from "@/components/controls/input/BmEnumDdl.vue";
import BmDropdown from "@/components/controls/input/BmDropdown.vue";
import BmDate from "@/components/controls/input/BmDate.vue";
import BmTime from "@/components/controls/input/BmTime.vue";
import BmSwitch from "@/components/controls/input/BmSwitch.vue";
import BmNumber from "@/components/controls/input/BmNumber.vue";
import BmTextArea from "@/components/controls/input/BmTextArea.vue";
import type { PlannedSalesVisit } from "@/app/services/generated/salesvisitService";
import { computed, onBeforeMount, watch } from "vue";
import { EnumSalesVisitAssociatedEntityType } from "@/app/services/generated/enums/enumSalesVisitAssociatedEntityType.generated";
import { EnumTaskRepeatBasis } from "@/app/services/generated/enums/enumTaskRepeatBasis.generated";
import useSalesVisitData, {
  getRepeatBasisText,
} from "@/components/pages/orders/salesvisits/useSalesVisitData";
import { userService } from "@/app/services/extensions/userServiceExtended";
import api from "@/app/api";
import { salesVisitService } from "@/app/services/generated/salesvisitService";
import {
  makeRef,
  shallowReadonlyObjRef,
} from "@/components/composables/reactivityTypes";
import type { User } from "@/app/services/generated/userService";
import type { LocalDate } from "@js-joda/core";
import { keys } from "@/plugins/i18n";

interface PlannedSalesVisitHeaderProps {
  isEditing?: boolean;
  value: PlannedSalesVisit;
  headerSize?: FlowLayoutSize;
}

interface PlannedSalesVisitHeaderEvents {
  (eventName: "input", salesVisit: PlannedSalesVisit): void;
}

const props = withDefaults(defineProps<PlannedSalesVisitHeaderProps>(), {
  isEditing: false,
  headerSize: "full-width",
});

const emit = defineEmits<PlannedSalesVisitHeaderEvents>();

const internalSalesVisit = computed({
  get: () => props.value,
  set: (newSalesVisit: PlannedSalesVisit) => {},
});
const users = makeRef<readonly User[]>([]);
const nextVisitDate = shallowReadonlyObjRef<LocalDate | null>(null);
const { lastOrderDate, lastVisitDate, cache } = useSalesVisitData(
  computed(() => internalSalesVisit.value.associatedEntityType),
  computed(() => internalSalesVisit.value.associatedEntityId),
  computed(() => internalSalesVisit.value.userId),
  computed(() => internalSalesVisit.value.date)
);

const headerSizeDefinition = computed(() => {
  return flowLayoutSizes[props.headerSize];
});

const maxRepeatParameter = computed(() => {
  if (
    internalSalesVisit.value.repeatBasis ===
    EnumTaskRepeatBasis.nthMatchingDayOfEachMonth
  ) {
    return 4;
  }

  return undefined;
});

const repeatParameterIsRelevant = computed(() => {
  return (
    internalSalesVisit.value.repeatBasis === EnumTaskRepeatBasis.everyNWeeks ||
    internalSalesVisit.value.repeatBasis === EnumTaskRepeatBasis.everyNMonths ||
    internalSalesVisit.value.repeatBasis ===
      EnumTaskRepeatBasis.nthMatchingDayOfEachMonth
  );
});

async function loadDependencies() {
  [users.value] = await Promise.all([
    userService.getAllUsersWithPremierUsersHidden(api.tenantGroupId),
    cache.update(),
  ]);
}

onBeforeMount(loadDependencies);

watch(
  () => props.value,
  () => {
    internalSalesVisit.value = props.value.clone();
  }
);

watch(internalSalesVisit, () => {
  emit("input", internalSalesVisit.value);
});

watch(
  () => [
    internalSalesVisit.value.date,
    internalSalesVisit.value.repeatBasis,
    internalSalesVisit.value.repeatParameter,
  ],
  async () => {
    const isVisitExists = !internalSalesVisit.value.id.isEmpty();
    const isVisitHasAssociatedEntity =
      !internalSalesVisit.value.associatedEntityId.isEmpty();
    const isVisitAssignedToUser = !internalSalesVisit.value.userId.isEmpty();

    nextVisitDate.value =
      isVisitExists && isVisitHasAssociatedEntity && isVisitAssignedToUser
        ? await salesVisitService.getNextVisitDateForPlannedSalesVisit(
            api.tenantId,
            internalSalesVisit.value
          )
        : null;
  },
  { immediate: true }
);
</script>

<template>
  <v-container fluid pa-0>
    <v-row>
      <v-col>
        <bm-form-flow-layout v-bind="headerSizeDefinition">
          <bm-enum-ddl
            v-model="internalSalesVisit.associatedEntityType"
            :t="$m.keys.sales_visit_associated_type"
            :enum-type="EnumSalesVisitAssociatedEntityType"
            :read-only="!isEditing"
            :rules="[$ruleSet.required()]"
          />
          <bm-dropdown
            v-if="
              internalSalesVisit.associatedEntityType ===
              EnumSalesVisitAssociatedEntityType.outlet
            "
            v-model="internalSalesVisit.associatedEntityId"
            :t="keys.sales_visit_outlet"
            :read-only="!isEditing"
            :items="cache.outlets.allReactive.value"
            item-value="id"
            item-text="displayName"
            :rules="[$ruleSet.required()]"
          />
          <bm-dropdown
            v-if="
              internalSalesVisit.associatedEntityType ===
              EnumSalesVisitAssociatedEntityType.group
            "
            v-model="internalSalesVisit.associatedEntityId"
            :t="keys.sales_visit_group"
            :read-only="!isEditing"
            :items="cache.groups.allReactive.value"
            item-value="id"
            item-text="name"
            :rules="[$ruleSet.required()]"
          />
          <bm-dropdown
            v-if="
              internalSalesVisit.associatedEntityType ===
              EnumSalesVisitAssociatedEntityType.lead
            "
            v-model="internalSalesVisit.associatedEntityId"
            :t="keys.sales_visit_lead"
            :read-only="!isEditing"
            :items="cache.leads.allReactive.value"
            item-value="id"
            item-text="businessName"
            :rules="[$ruleSet.required()]"
          />
          <bm-dropdown
            v-model="internalSalesVisit.userId"
            :t="keys.sales_visits_user"
            :items="users"
            :read-only="!isEditing"
            item-value="id"
            item-text="name"
            :rules="[$ruleSet.required()]"
          />
          <bm-date
            v-model="internalSalesVisit.date"
            :t="keys.sales_visit_date"
            :read-only="!isEditing"
            :rules="[$ruleSet.required()]"
          />
          <bm-time
            v-model="internalSalesVisit.plannedTime"
            :t="keys.sales_visit_planned_time"
            :read-only="!isEditing"
          />
          <bm-switch
            v-model="internalSalesVisit.isPlannedTimeFlexible"
            :t="keys.sales_visit_is_planned_start_time_flexible"
            :read-only="!isEditing"
          />
          <bm-number
            v-model="internalSalesVisit.durationMinutes"
            :t="keys.sales_visit_duration"
            :read-only="!isEditing"
            :min="0"
            :rules="[$ruleSet.notNegativeNumber()]"
            :suffix="$m.units_mins_suffix"
            integral
          />
          <bm-text-area
            v-model="internalSalesVisit.notes"
            :t="keys.sales_visit_notes"
            :read-only="!isEditing"
          />
          <bm-number
            v-if="repeatParameterIsRelevant"
            v-model="internalSalesVisit.repeatParameter"
            :t="keys.sales_visit_repeat_parameter"
            :read-only="!isEditing"
            :min="1"
            :max="maxRepeatParameter"
            :rules="[$ruleSet.numberInRange(1, maxRepeatParameter)]"
            integral
          />
          <bm-dropdown
            v-model="internalSalesVisit.repeatBasis"
            :t="keys.sales_visit_repeat_basis"
            :read-only="!isEditing"
            :items="EnumTaskRepeatBasis.getValues()"
            :item-text="
              (basis) => getRepeatBasisText(internalSalesVisit, basis)
            "
            :item-value="(basis) => basis.key"
            :sort-items="false"
            :searchable="false"
            return-object
          />
          <v-col cols="12">
            <v-divider />
          </v-col>
          <bm-date
            v-if="
              internalSalesVisit.associatedEntityType !=
              EnumSalesVisitAssociatedEntityType.lead
            "
            v-model="lastOrderDate"
            :t="keys.sales_visit_last_order"
            :read-only="true"
          />
          <bm-date
            v-model="lastVisitDate"
            :t="keys.sales_visit_last_visit"
            :read-only="true"
          />
          <bm-date
            v-model="nextVisitDate"
            :t="keys.sales_visit_next_visit"
            :read-only="true"
          />
        </bm-form-flow-layout>
      </v-col>
    </v-row>
  </v-container>
</template>
